import { useSearchParams } from 'react-router-dom'

import { Segmented } from '@/components/activity/activity-panel'
import { UserActivityPane } from '@/components/user-activity/user-activity-pane'
import { UserAuditFeed } from '@/components/user-activity/user-audit-feed'
import { UserIdentity } from '@/components/user-activity/user-identity'
import { USER_ACTIVITY_HELP_TEXT } from '@/utils/user-activity'

type UserActivityView = 'activity' | 'audit'

const isView = (value: string | null): value is UserActivityView =>
  value === 'activity' || value === 'audit'

type UserActivityTabProps = {
  userId?: string
  orgId?: string
  name?: string
  email?: string
}

/**
 * User details → Activity. Same content as the org Metrics drawer (shared
 * UserActivityPane / UserAuditFeed), laid out as a self-contained,
 * screenshot-friendly card.
 */
export const UserActivityTab = ({
  userId,
  orgId,
  name,
  email
}: UserActivityTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const utab = searchParams.get('utab')
  const view: UserActivityView = isView(utab) ? utab : 'activity'

  // push (not replace) so back/forward switches views; keep tab, orgId & other params
  const setView = (value: UserActivityView) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      if (value === 'activity') next.delete('utab')
      else next.set('utab', value)

      return next
    })
  }

  if (!userId || !orgId) return null

  return (
    <section
      aria-label="User activity"
      className="max-w-4xl space-y-6 rounded-xl bg-muted/20 p-6 text-foreground"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <UserIdentity email={email} name={name} orgId={orgId} userId={userId} />
        <Segmented
          ariaLabel="User activity view"
          onChange={setView}
          options={[
            { value: 'activity', label: 'Activity' },
            { value: 'audit', label: 'Audit log' }
          ]}
          value={view}
        />
      </div>

      {view === 'audit' ? (
        <UserAuditFeed orgId={orgId} userId={userId} />
      ) : (
        <UserActivityPane orgId={orgId} userId={userId} />
      )}

      <footer className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground/70">
        <span>{USER_ACTIVITY_HELP_TEXT}</span>
        <span className="tabular-nums">
          Snapshot {new Date().toLocaleString()}
        </span>
      </footer>
    </section>
  )
}
